import {
  trackTmCollectionCreated,
  trackTmCollectionDeleted,
  trackTmEntryCreated,
  trackTmEntryDeleted,
  trackTmEntryMoved,
  trackTmEntryUpdated,
} from '@src/analytics/EventTracker';
import {
  useDatoPerson,
  useTranslationMemoryEntryStore,
  useTranslationMemoryImportStore,
  useTranslationMemoryStore,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import PlusIcon from '@src/svgs/PlusIcon';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import CollectionList, {
  ALL_COLLECTIONS_ID,
  DEFAULT_COLLECTION_ID,
} from './translationMemory/CollectionList';
import {
  distinctLanguages,
  filterEntries,
} from './translationMemory/entryFilters';
import EntryFormModal from './translationMemory/EntryFormModal';
import EntryTable from './translationMemory/EntryTable';
import FilterBar from './translationMemory/FilterBar';
import { PrimaryButton } from './translationMemory/formControls';
import ImportHistoryList from './translationMemory/ImportHistoryList';
import ImportModal from './translationMemory/ImportModal';
import MatrixView from './translationMemory/MatrixView';
import ViewToggle, {
  type TranslationMemoryViewMode,
} from './translationMemory/ViewToggle';

import type { TranslationMemoryCollectionInput } from '@src/repositories/TranslationMemoryCollectionRepository';
import type {
  TranslationMemoryEntry,
  TranslationMemoryEntryInput,
} from '@src/repositories/TranslationMemoryEntryRepository';
import type { TranslationMemoryImport } from '@src/repositories/TranslationMemoryImportRepository';
import type { TranslationMemoryEntriesState } from '@src/stores-v2/TranslationMemoryEntryStore';
import type { TranslationMemoryImportsState } from '@src/stores-v2/TranslationMemoryImportStore';
import type { TranslationMemoryCollectionsState } from '@src/stores-v2/TranslationMemoryStore';

/**
 * Settings → "Translation Memory" tab: curate the workspace glossary. The left
 * pane is the flat collection list (create / rename / delete, PLAT-10844); the
 * right pane is the entry table for the selected collection with full entry CRUD
 * (create / edit / delete, the "Do not translate" affordance, and moving entries
 * between collections — PLAT-10845). Collections and entries are each cached per
 * showcase, so reopening the tab doesn't refetch.
 */
const TranslationMemorySection = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const store = useTranslationMemoryStore();
  const entryStore = useTranslationMemoryEntryStore();
  const importStore = useTranslationMemoryImportStore();
  const datoPerson = useDatoPerson();
  const showcaseId = videoCreator.organization?.id;
  const languages = videoCreator.organization?.supportedLanguages ?? [];
  // The showcase's primary language (defaultLanguage is a BCP-47 value, e.g.
  // "en-us"); resolve it to a supported-language id so the source filter can
  // default to it.
  const defaultLanguage = videoCreator.organization?.defaultLanguage;
  const primarySourceLanguageId = defaultLanguage
    ? (languages.find(
        l => l.value?.toLowerCase() === defaultLanguage.toLowerCase(),
      )?.id ?? null)
    : null;

  const [selectedId, setSelectedId] = useState<string>(ALL_COLLECTIONS_ID);
  const [importOpen, setImportOpen] = useState(false);
  const [reimportFile, setReimportFile] = useState<File | null>(null);

  // Shared view-mode + filter/base-language state (E17), lifted up out of
  // EntryTable so the table and the matrix (E16) read one bar (PLAT-10847).
  const [viewMode, setViewMode] = useState<TranslationMemoryViewMode>('table');
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  // The create/edit entry modal, owned here so the Add entry action can live in
  // the shared toolbar alongside the view toggle and filter bar.
  const [entryModal, setEntryModal] = useState<
    { mode: 'create' } | { mode: 'edit'; entry: TranslationMemoryEntry } | null
  >(null);

  // Seed the source/base-language filter to the showcase's primary language
  // once it's known (the organization can load after mount); a ref guards it so
  // a later manual "All source languages" choice isn't overridden.
  const seededSource = useRef(false);
  useEffect(() => {
    if (!seededSource.current && primarySourceLanguageId) {
      setSourceFilter(primarySourceLanguageId);
      seededSource.current = true;
    }
  }, [primarySourceLanguageId]);

  useEffect(() => {
    if (!showcaseId) return;
    void store.loadForShowcase(showcaseId);
    void entryStore.loadForShowcase(showcaseId);
    void importStore.loadForShowcase(showcaseId);
  }, [showcaseId, store, entryStore, importStore]);

  const state: TranslationMemoryCollectionsState = showcaseId
    ? store.getState(showcaseId)
    : { status: 'idle', collections: [], error: null };

  const entryState: TranslationMemoryEntriesState = showcaseId
    ? entryStore.getState(showcaseId)
    : { status: 'idle', entries: [], error: null, truncated: false };

  const importState: TranslationMemoryImportsState = showcaseId
    ? importStore.getState(showcaseId)
    : { status: 'idle', imports: [], error: null };

  const author = datoPerson.person?.id
    ? { id: datoPerson.person.id, name: datoPerson.person.name }
    : null;

  const isAllView = selectedId === ALL_COLLECTIONS_ID;
  // The collection a new entry prefills to (null = Default). The All view isn't
  // scoped to one collection, so a new entry there defaults to Default.
  const selectedCollectionId =
    isAllView || selectedId === DEFAULT_COLLECTION_ID ? null : selectedId;
  const visibleEntries = isAllView
    ? entryState.entries
    : entryState.entries.filter(e => e.collectionId === selectedCollectionId);
  const collectionOptions = state.collections.map(c => ({
    id: c.id,
    name: c.name,
    description: c.description ?? '',
  }));
  const collectionName = isAllView
    ? 'All translations'
    : selectedId === DEFAULT_COLLECTION_ID
      ? 'Default'
      : (state.collections.find(c => c.id === selectedId)?.name ??
        'Collection');

  // Resolve an entry's collection id (null = Default) to a display name — used
  // as the matrix cell provenance and the "All translations" grouping.
  const resolveCollectionName = (collectionId: string | null) =>
    collectionId == null
      ? 'Default'
      : (state.collections.find(c => c.id === collectionId)?.name ??
        'Collection');

  // The shared bar's language options reflect only the languages the current
  // collection's entries actually use.
  const sourceLanguageOptions = distinctLanguages(
    visibleEntries.map(e => e.sourceLanguage),
  );
  const targetLanguageOptions = distinctLanguages(
    visibleEntries.map(e => e.translationLanguage),
  );

  // A filter carried over from another collection (or the primary-language
  // seed) can name a language the current collection has no entries in — the
  // picker then renders blank and the table falsely reads as "no entries
  // match". Drop any active filter the current (collection-scoped) entries
  // don't offer once they're loaded. (This guard lived in EntryTable before the
  // filter state was lifted here in PLAT-10847; it has to move with the state.)
  useEffect(() => {
    if (entryState.status !== 'ready') return;
    if (sourceFilter && !sourceLanguageOptions.some(l => l.id === sourceFilter))
      setSourceFilter('');
    if (targetFilter && !targetLanguageOptions.some(l => l.id === targetFilter))
      setTargetFilter('');
  }, [
    selectedId,
    entryState.status,
    sourceLanguageOptions,
    targetLanguageOptions,
    sourceFilter,
    targetFilter,
  ]);

  // The matrix always pivots on a concrete base language; fall back to the
  // primary (or first present) source language when "All source languages" is
  // selected in the table.
  const baseLanguageId =
    sourceFilter ||
    primarySourceLanguageId ||
    sourceLanguageOptions[0]?.id ||
    '';
  const baseLanguageName =
    languages.find(l => l.id === baseLanguageId)?.caption ??
    sourceLanguageOptions.find(l => l.id === baseLanguageId)?.caption ??
    'Source term';

  const tableEntries = filterEntries(visibleEntries, {
    search,
    sourceId: sourceFilter,
    targetId: targetFilter,
  });
  // The matrix renders every target language as a column, so it ignores the
  // target filter and pins the base (source) language.
  const matrixEntries = filterEntries(visibleEntries, {
    search,
    sourceId: baseLanguageId,
    targetId: '',
  });

  // Entering the matrix commits the concrete base language into the shared
  // control (the matrix selector drops the "All source languages" option).
  const changeView = (next: TranslationMemoryViewMode) => {
    if (next === 'matrix' && !sourceFilter && baseLanguageId) {
      setSourceFilter(baseLanguageId);
    }
    setViewMode(next);
  };

  // Shared analytics shape for the entry events (PLAT-10853). Codes and
  // booleans only — a source term / translation never goes into an event; DNT
  // is the identity convention (E4), the same test `EntryFormModal` seeds its
  // toggle from.
  const entryEventProps = (input: TranslationMemoryEntryInput) => ({
    showcase_id: showcaseId,
    collection_id: input.collectionId,
    source_lang: input.sourceLanguage.value ?? undefined,
    target_lang: input.translationLanguage?.value ?? null,
    is_dnt: input.translation === input.sourceTerm,
    has_comment: !!input.comment?.trim(),
  });

  // Collection mutation handlers resolve `null` on success or a user-facing
  // error message (surfaced inline) — no silent failures (S3).
  const create = async (input: TranslationMemoryCollectionInput) => {
    if (!showcaseId) return 'No workspace selected.';
    const result = await store.createCollection(showcaseId, input, author);
    if (result.ok) {
      trackTmCollectionCreated({
        showcase_id: showcaseId,
        collection_id: result.value.id,
        has_description: !!input.description?.trim(),
        surface: 'collection_list',
      });
      setSelectedId(result.value.id);
      return null;
    }
    return result.error.message || 'Failed to create collection.';
  };

  const rename = async (
    id: string,
    input: TranslationMemoryCollectionInput,
  ) => {
    if (!showcaseId) return 'No workspace selected.';
    const result = await store.updateCollection(showcaseId, id, input, author);
    return result.ok
      ? null
      : result.error.message || 'Failed to rename collection.';
  };

  const remove = async (id: string) => {
    if (!showcaseId) return 'No workspace selected.';
    // Count the collection's entries before the delete — afterwards the store
    // has already dropped them from the cache.
    const entryCount = entryState.entries.filter(
      e => e.collectionId === id,
    ).length;
    const result = await store.deleteCollection(showcaseId, id);
    if (result.ok) {
      trackTmCollectionDeleted({
        showcase_id: showcaseId,
        collection_id: id,
        entry_count: entryCount,
      });
      if (selectedId === id) setSelectedId(DEFAULT_COLLECTION_ID);
      return null;
    }
    return result.error.message || 'Failed to delete collection.';
  };

  // Entry mutation handlers, same no-silent-failure contract (S3).
  const createEntry = async (input: TranslationMemoryEntryInput) => {
    if (!showcaseId) return 'No workspace selected.';
    const result = await entryStore.createEntry(showcaseId, input, author);
    if (!result.ok) return result.error.message || 'Failed to add entry.';
    trackTmEntryCreated(entryEventProps(input));
    return null;
  };

  const editEntry = async (id: string, input: TranslationMemoryEntryInput) => {
    if (!showcaseId) return 'No workspace selected.';
    // Read the pre-edit collection before the store merges the new one in, so a
    // reassignment can be reported as a move on top of the update (PLAT-10853).
    const previousCollectionId =
      entryState.entries.find(e => e.id === id)?.collectionId ?? null;
    const result = await entryStore.updateEntry(showcaseId, id, input, author);
    if (!result.ok) return result.error.message || 'Failed to save entry.';
    trackTmEntryUpdated({ ...entryEventProps(input), surface: 'settings' });
    if (previousCollectionId !== input.collectionId) {
      trackTmEntryMoved({
        showcase_id: showcaseId,
        from_collection_id: previousCollectionId,
        to_collection_id: input.collectionId,
        surface: 'settings',
      });
    }
    return null;
  };

  // Also the bulk-delete path — `EntryTable.runBulkDelete` loops this same
  // handler, so a bulk delete of N entries fires N events (PLAT-10853 S3).
  const removeEntry = async (id: string) => {
    if (!showcaseId) return 'No workspace selected.';
    const collectionId =
      entryState.entries.find(e => e.id === id)?.collectionId ?? null;
    const result = await entryStore.deleteEntry(showcaseId, id);
    if (!result.ok) return result.error.message || 'Failed to delete entry.';
    trackTmEntryDeleted({
      showcase_id: showcaseId,
      collection_id: collectionId,
    });
    return null;
  };

  const openImport = () => {
    setReimportFile(null);
    setImportOpen(true);
  };

  // Create a collection from inside the import modal; returns the new id so the
  // modal can select it (mirrors the collection list's create).
  const createCollectionInline = async (name: string, description: string) => {
    if (!showcaseId) return { error: 'No workspace selected.' };
    const result = await store.createCollection(
      showcaseId,
      { name, description },
      author,
    );
    if (!result.ok)
      return { error: result.error.message || 'Failed to create collection.' };
    trackTmCollectionCreated({
      showcase_id: showcaseId,
      collection_id: result.value.id,
      has_description: !!description.trim(),
      surface: 'import_modal',
    });
    return { id: result.value.id };
  };

  // Re-import (DS6): fetch the retained file and re-run the full import flow
  // (through the same preview → confirm gate). Silently no-ops if the asset
  // can't be fetched — the user can still import a fresh file.
  const reimport = async (imp: TranslationMemoryImport) => {
    if (!imp.fileUrl) return;
    try {
      const blob = await fetch(imp.fileUrl).then(r => r.blob());
      setReimportFile(new File([blob], imp.fileName, { type: blob.type }));
      setImportOpen(true);
    } catch {
      setReimportFile(null);
      setImportOpen(true);
    }
  };

  return (
    <Section>
      <header>
        <div className="titles">
          <h2>Translation Memory</h2>
          <p>
            Curate the terms this workspace pins during translation. Organize
            them into collections; every translation honors them automatically.
          </p>
        </div>
        <ImportButton type="button" onClick={openImport} disabled={!showcaseId}>
          Import glossary
        </ImportButton>
      </header>
      <Layout>
        <CollectionList
          collections={state.collections}
          status={state.status}
          error={state.error}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onCreate={create}
          onRename={rename}
          onDelete={remove}
          retry={() =>
            showcaseId &&
            void store.loadForShowcase(showcaseId, { force: true })
          }
        />
        <RightColumn>
          {/* PLAT-10871: the entry read is capped, and so is the backend's own
              translation-memory load (same 5,000 ceiling), so an over-cap
              workspace loses terms in BOTH places — say so rather than let a
              short list read as the whole glossary. */}
          {entryState.truncated && (
            <TruncationNote>
              Showing the first {entryState.entries.length.toLocaleString()}{' '}
              entries — this workspace has more than can be loaded at once.
              Entries past this point aren&apos;t listed here and aren&apos;t
              honored during translation. Split the glossary across workspaces,
              or remove entries you no longer need.
            </TruncationNote>
          )}
          <ViewBar>
            <ViewToggle mode={viewMode} onChange={changeView} />
            <FilterBar
              search={search}
              onSearchChange={setSearch}
              sourceFilter={sourceFilter}
              onSourceChange={setSourceFilter}
              sourceLanguages={sourceLanguageOptions}
              targetFilter={targetFilter}
              onTargetChange={setTargetFilter}
              targetLanguages={targetLanguageOptions}
              showTargetFilter={viewMode === 'table'}
              showAllSourceOption={viewMode === 'table'}
              sourceLabel={
                viewMode === 'matrix' ? 'Base language' : 'Source language'
              }
            />
            {viewMode === 'table' && (
              <AddButton
                type="button"
                onClick={() => setEntryModal({ mode: 'create' })}
              >
                <PlusIcon
                  width="12"
                  height="12"
                  viewBox="0 0 40 40"
                  strokeColor="currentColor"
                />
                Add entry
              </AddButton>
            )}
          </ViewBar>
          {viewMode === 'table' ? (
            <EntryTable
              collectionName={collectionName}
              collectionKey={selectedId}
              entries={tableEntries}
              hasAnyEntries={visibleEntries.length > 0}
              showCollection={isAllView}
              resolveCollectionName={resolveCollectionName}
              status={entryState.status}
              error={entryState.error}
              onEditEntry={entry => setEntryModal({ mode: 'edit', entry })}
              onDelete={removeEntry}
              retry={() =>
                showcaseId &&
                void entryStore.loadForShowcase(showcaseId, { force: true })
              }
            />
          ) : (
            <MatrixView
              entries={matrixEntries}
              baseLanguageName={baseLanguageName}
              collectionName={collectionName}
              resolveCollectionName={resolveCollectionName}
              status={entryState.status}
              error={entryState.error}
              retry={() =>
                showcaseId &&
                void entryStore.loadForShowcase(showcaseId, { force: true })
              }
            />
          )}
        </RightColumn>
      </Layout>

      <ImportHistoryList
        imports={importState.imports}
        status={importState.status}
        error={importState.error}
        retry={() =>
          showcaseId &&
          void importStore.loadForShowcase(showcaseId, { force: true })
        }
        onReimport={reimport}
      />

      {entryModal && (
        <EntryFormModal
          mode={entryModal.mode}
          languages={languages}
          collections={collectionOptions}
          defaultSourceLanguageId={primarySourceLanguageId}
          initialCollectionId={selectedCollectionId}
          entry={entryModal.mode === 'edit' ? entryModal.entry : undefined}
          onClose={() => setEntryModal(null)}
          onSubmit={input =>
            entryModal.mode === 'edit'
              ? editEntry(entryModal.entry.id, input)
              : createEntry(input)
          }
        />
      )}

      {importOpen && showcaseId && (
        <ImportModal
          showcaseId={showcaseId}
          languages={languages}
          collections={collectionOptions}
          defaultSourceLanguageId={primarySourceLanguageId}
          defaultCollectionId={selectedCollectionId ?? ''}
          author={author}
          initialFile={reimportFile}
          onCreateCollection={createCollectionInline}
          onClose={() => setImportOpen(false)}
        />
      )}
    </Section>
  );
});

export default TranslationMemorySection;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;

  > header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;

    > .titles {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    h2 {
      font-size: 20px;
      font-weight: var(--font-weight-bold);
      color: var(--foreground);
      margin: 0;
    }

    p {
      font-size: var(--text-sm);
      color: var(--text-secondary);
      margin: 0;
    }
  }
`;

const ImportButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  height: var(--btn-h-sm);
  white-space: nowrap;
  background: none;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  color: var(--foreground);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--primary);
    color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 240px) minmax(0, 1fr);
  gap: 24px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// The right pane: the shared view toggle + filter bar above the active view
// (entry table or matrix).
const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

// Over-cap entry load (PLAT-10871). "Needs attention" yellow rather than
// destructive red — nothing is broken or lost, but the list on screen is not the
// whole glossary. Matches the warning banner idiom used in the translation
// modals.
const TruncationNote = styled.p`
  margin: 0;
  padding: 10px 12px;
  font-size: var(--text-sm);
  color: var(--warning, #b58105);
  background-color: color-mix(
    in srgb,
    var(--warning, #b58105) 12%,
    transparent
  );
  border: 1px solid var(--warning, #b58105);
  border-radius: var(--radius-input);
`;

// One streamlined row: view toggle · filter bar (grows) · Add entry. Never
// wraps — the filter bar's search shrinks to keep everything on a single line.
// Matches the collection list's PanelHeader height so the entry table lines up
// at the top with the collection list.
const ViewBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: nowrap;
  min-width: 0;
  min-height: calc(var(--btn-h-sm) + 20px);

  /* The filter bar grows so the Add entry button stays pinned to the right. */
  > *:nth-child(2) {
    flex: 1;
    min-width: 0;
  }
`;

const AddButton = styled(PrimaryButton)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;
